<template>
  <div style="overflow-x:hidden;">

    <!-- ═══ PAGE HERO ═══ -->
    <div class="nf-page-hero">
      <h1>Request a Service</h1>
      <ul class="nf-breadcrumb">
        <li><router-link to="/">Home</router-link></li>
        <li class="sep"><i class="fas fa-chevron-right" style="font-size:.65rem;"></i></li>
        <li class="active">Get a Quote</li>
      </ul>
    </div>

    <!-- ═══ QUOTE FORM ═══ -->
    <section class="py-6 px-3 px-lg-5 bg-white">
      <div class="container-fluid">
        <div class="text-center mb-5">
          <span class="section-label">Work With Us</span>
          <h2 class="section-title">{{ quoteContent.title || 'Request a Service' }}</h2>
          <hr class="section-divider" />
        </div>

        <div class="row g-5 align-items-center justify-content-center">

          <!-- Form panel -->
          <div class="col-12 col-lg-7">
            <div class="nf-form-panel">
              <h4 class="mb-1">{{ quoteContent.form_title || 'Tell us what you need' }}</h4>
              <p class="text-muted small mb-4">{{ quoteContent.form_description || "Fill in the form and we'll get back to you promptly." }}</p>

              <form @submit.prevent="submitQuote">
                <div class="row g-3">
                  <div class="col-12 col-sm-6">
                    <div class="form-floating">
                      <input type="text" class="form-control" id="q-name" placeholder="John Doe" v-model="fullName" required />
                      <label for="q-name">Full Name</label>
                    </div>
                  </div>
                  <div class="col-12 col-sm-6">
                    <div class="form-floating">
                      <input type="email" class="form-control" id="q-email" placeholder="email@example.com" v-model="email" required />
                      <label for="q-email">Email Address</label>
                    </div>
                  </div>
                  <div class="col-12 col-sm-6">
                    <div class="form-floating">
                      <select class="form-select" id="q-service" v-model="selectedServiceId"
                        :disabled="customServiceName.trim() !== ''"
                        :required="customServiceName.trim() === ''">
                        <option disabled value="">Choose a service</option>
                        <option v-for="s in services" :key="s.id" :value="s.id">{{ s.name }}</option>
                      </select>
                      <label for="q-service">Select a Service</label>
                    </div>
                  </div>
                  <div class="col-12 col-sm-6">
                    <div class="form-floating">
                      <textarea class="form-control" id="q-custom" placeholder="Describe your need"
                        v-model="customServiceName"
                        :disabled="selectedServiceId !== ''"
                        style="height:100px;"></textarea>
                      <label for="q-custom">Or Describe Your Need</label>
                    </div>
                  </div>
                  <div class="col-12">
                    <button class="btn btn-primary w-100 py-3 rounded-pill" type="submit" :disabled="loading">
                      <span v-if="!loading"><i class="fas fa-paper-plane me-2"></i>Submit Request</span>
                      <span v-else><i class="fas fa-spinner fa-spin me-2"></i>Sending…</span>
                    </button>
                  </div>
                </div>
              </form>

              <div v-if="message" class="mt-3 p-3 rounded-3 small fw-semibold"
                :class="success ? 'nf-alert-success' : 'nf-alert-error'">
                <i :class="success ? 'fas fa-check-circle me-2' : 'fas fa-exclamation-circle me-2'"></i>{{ message }}
              </div>
            </div>
          </div>

          <!-- Side info -->
          <div class="col-12 col-lg-5">
            <img :src="'/img/quote.jpg'" class="img-fluid rounded-4 shadow-lg mb-4 d-none d-lg-block"
              alt="Consultation" style="object-fit:cover;height:260px;width:100%;" />

            <div class="nf-why-us">
              <h5 class="mb-3 text-primary">Why Work With Us?</h5>
              <div class="nf-why-item" v-for="(item, i) in whyUs" :key="i">
                <div class="nf-why-icon"><i :class="item.icon"></i></div>
                <div>
                  <h6 class="mb-0">{{ item.title }}</h6>
                  <p class="mb-0 small text-muted">{{ item.text }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
export default {
  name: 'QuoteView',
  data() {
    return {
      fullName: '',
      email: '',
      selectedServiceId: '',
      customServiceName: '',
      services: [],
      quoteContent: { title: '', description: '' },
      loading: false,
      message: '',
      success: false,
      whyUs: [
        { icon:'fas fa-certificate', title:'Evidence-Based Approach',     text:'All our solutions are grounded in rigorous research and data.' },
        { icon:'fas fa-users',       title:'Community-Centred Practice',  text:'We prioritize the voices and needs of local communities.' },
        { icon:'fas fa-globe-africa',title:'Local Expertise, Global Standards', text:'Kenyan-rooted but internationally aligned with best practices.' },
        { icon:'fas fa-handshake',   title:'Long-Term Partnerships',      text:'We build lasting relationships beyond single project engagements.' },
      ],
    };
  },
  async mounted() {
    try {
      const [svcRes, contentRes] = await Promise.all([
        fetch('https://admin.nexusfocusconsultancy.com/quote/api/services/'),
        fetch('https://admin.nexusfocusconsultancy.com/quote/api/quote-content/'),
      ]);
      this.services     = await svcRes.json();
      this.quoteContent = await contentRes.json();
    } catch (e) {
      this.message = 'Failed to load content.';
    }
  },
  methods: {
    async submitQuote() {
      this.loading = true;
      this.message = '';
      this.success = false;
      if (!this.selectedServiceId && !this.customServiceName.trim()) {
        this.message = 'Please select a service or describe your need.';
        this.loading = false;
        return;
      }
      try {
        const payload = { full_name: this.fullName, email: this.email };
        if (this.customServiceName.trim()) payload.custom_service_name = this.customServiceName.trim();
        else payload.service_id = this.selectedServiceId;

        const res = await fetch('https://admin.nexusfocusconsultancy.com/quote/api/quote/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Submission failed.');
        }
        this.message = 'Your request was sent successfully. We\'ll be in touch shortly!';
        this.success = true;
        this.fullName = ''; this.email = ''; this.selectedServiceId = ''; this.customServiceName = '';
      } catch (e) {
        this.message = e.message;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Why Us sidebar */
.nf-why-us { display:flex; flex-direction:column; gap:1rem; }
.nf-why-item {
  display:flex; align-items:flex-start; gap:1rem;
  padding:1rem 1.25rem;
  background:var(--nf-light,#f7f9f7);
  border-radius:10px;
  border-left:3px solid var(--nf-green,#1a6b4a);
}
.nf-why-icon {
  width:36px; height:36px; flex-shrink:0;
  border-radius:50%;
  background:var(--nf-green-tint,#e7f4ed);
  display:flex; align-items:center; justify-content:center;
  font-size:.9rem; color:var(--nf-green,#1a6b4a);
}
.nf-alert-success { background:#e7f4ed; color:#1a6b4a; }
.nf-alert-error   { background:#fce8e8; color:#c0392b; }
</style>
